import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import { isApiClientError, type PlatformChallenge, type PlatformSession } from '@sm/api';
import { totpCodeSchema } from '@sm/core';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useReducer, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { LanguageMenu, ThemeToggleButton } from '@/components/shell/LanguageMenu';
import { CodeInput as OtpInput } from '@/components/ui/CodeInput';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Dialog';
import { OrDivider, ProviderButton } from '@/features/auth/OidcButtons';
import { Field, Input } from '@/components/ui/Field';
import { useTheme } from '@/hooks/usePreferences';
import { unwrap } from '@/lib/api';
import { platformApi } from '@/lib/platformApi';
import { pk } from '@/lib/platformQueries';

import {
  initialLoginState,
  platformLoginReducer,
  safePlatformRedirect,
  type PlatformLoginState,
} from './loginMachine';
import { BackupCodes, PlatformBadge, TotpEnrollment } from './ui';

const credentialsSchema = z.object({
  email: z.string().trim().min(1, 'validation.required').email('validation.email'),
  password: z.string().min(1, 'validation.required'),
});
type Credentials = z.input<typeof credentialsSchema>;
type CodeInput = z.input<typeof totpCodeSchema>;

/** Prijava operatera platforme: lozinka → TOTP (ili obavezno uključivanje TOTP-a) → rezervni kodovi. */
export function PlatformLoginPage({
  embedded = false,
  initialChallenge,
  onRestart,
}: {
  embedded?: boolean;
  initialChallenge?: PlatformChallenge;
  onRestart?: () => void;
}) {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme(false);
  const qc = useQueryClient();
  const router = useRouter();
  const search = useRouterState({ select: (s) => s.location.search as { redirect?: string } });
  const [googlePreviewOpen, setGooglePreviewOpen] = useState(false);
  const [state, dispatch] = useReducer(
    platformLoginReducer,
    initialChallenge
      ? platformLoginReducer(initialLoginState, { type: 'challenge', challenge: initialChallenge })
      : initialLoginState,
  );

  useEffect(() => {
    document.title = t('panel:platform.pageTitle', { page: t('panel:platform.login.title') });
  }, [t]);

  useEffect(() => {
    if (state.step === 'done') router.history.push(safePlatformRedirect(search.redirect));
  }, [state.step, router, search.redirect]);

  return (
    <div className={embedded ? 'flex flex-col gap-5' : 'flex min-h-screen flex-col bg-bg2'}>
      {embedded ? null : (
        <header className="flex h-16 items-center justify-between gap-3 bg-navy px-5 text-white sm:px-8">
          <span className="flex items-center gap-3">
            <span className="font-heading text-xl leading-none font-semibold tracking-[-.02em] whitespace-nowrap">
              Smart Measure
            </span>
            <PlatformBadge />
          </span>
          <span className="flex items-center gap-2 [&_button]:border-white/30 [&_button]:text-white">
            <LanguageMenu />
            <ThemeToggleButton theme={theme} onToggle={toggle} />
          </span>
        </header>
      )}
      <main
        className={
          embedded ? undefined : 'flex flex-1 items-start justify-center px-4 py-12 sm:items-center'
        }
      >
        <div
          className={
            embedded
              ? 'flex flex-col gap-5'
              : 'flex w-full max-w-[440px] flex-col gap-5 rounded-2xl border border-line bg-surface p-7 shadow-card'
          }
        >
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-chip text-accent">
              <ShieldCheck size={22} />
            </span>
            <div className="flex flex-col">
              <h1 className="m-0 font-heading text-[22px] font-semibold">
                {t('panel:platform.login.title')}
              </h1>
              <span className="text-[13px] text-muted">{t('panel:platform.login.subtitle')}</span>
            </div>
          </div>
          <LoginStep
            state={state}
            onChallenge={(challenge) => dispatch({ type: 'challenge', challenge })}
            onVerified={(session) => {
              qc.setQueryData(pk.me, session.admin);
              dispatch({ type: 'verified', session });
            }}
            onCodesSaved={() => dispatch({ type: 'codesSaved' })}
            onRestart={() => (onRestart ? onRestart() : dispatch({ type: 'restart' }))}
          />
          {state.step === 'credentials' ? (
            <>
              <OrDivider />
              <ProviderButton
                provider="google"
                label={t('auth:oidc.button.login', { provider: 'Google' })}
                busy={false}
                disabled={false}
                onClick={() => setGooglePreviewOpen(true)}
              />
            </>
          ) : null}
        </div>
      </main>
      <Modal
        open={googlePreviewOpen}
        onOpenChange={setGooglePreviewOpen}
        title={t('auth:oidc.inProgress')}
        width={440}
        testId="platform-google-preview"
        footer={
          <Button onClick={() => setGooglePreviewOpen(false)}>{t('common:actions.close')}</Button>
        }
      >
        <p className="m-0 text-sm text-text2">{t('panel:platform.login.googleInProgress')}</p>
      </Modal>
    </div>
  );
}

function LoginStep({
  state,
  onChallenge,
  onVerified,
  onCodesSaved,
  onRestart,
}: {
  state: PlatformLoginState;
  onChallenge: Parameters<typeof CredentialsForm>[0]['onChallenge'];
  onVerified: Parameters<typeof CodeForm>[0]['onVerified'];
  onCodesSaved: () => void;
  onRestart: () => void;
}) {
  switch (state.step) {
    case 'credentials':
      return <CredentialsForm onChallenge={onChallenge} />;
    case 'totp':
      return (
        <CodeForm
          challengeToken={state.challenge.challengeToken}
          onVerified={onVerified}
          onRestart={onRestart}
        />
      );
    case 'enroll':
      return (
        <div className="flex flex-col gap-4">
          <TotpEnrollment enrollment={state.challenge.enrollment!} />
          <CodeForm
            challengeToken={state.challenge.challengeToken}
            onVerified={onVerified}
            onRestart={onRestart}
            enrolling
          />
        </div>
      );
    case 'backupCodes':
      return <BackupCodes codes={state.codes} onDone={onCodesSaved} />;
    case 'done':
      return null;
  }
}

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md bg-danger-bg px-3 py-2.5 text-[13px] font-medium text-danger"
      data-testid="platform-login-error"
    >
      <WarningCircle size={17} className="mt-px flex-none" />
      {message}
    </div>
  );
}

function CredentialsForm({ onChallenge }: { onChallenge: (c: PlatformChallenge) => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });
  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const ch = await unwrap(
        platformApi.POST('/platform/auth/login', {
          body: { email: v.email.trim(), password: v.password },
        }),
      );
      onChallenge(ch);
    } catch (e) {
      if (isApiClientError(e) && (e.status === 401 || e.status === 400))
        setError(t('panel:platform.login.invalid'));
      else if (isApiClientError(e)) setError(t(e.i18nKey));
      else setError(t('errors:network_error'));
    }
  });
  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      noValidate
      className="flex flex-col gap-4"
      data-testid="platform-login-form"
    >
      <ErrorLine message={error} />
      <Field
        label={t('panel:platform.login.email')}
        htmlFor="pl-email"
        error={errors.email?.message ? `errors:${errors.email.message}` : undefined}
      >
        <Input
          id="pl-email"
          type="email"
          autoComplete="username"
          autoFocus
          invalid={!!errors.email}
          data-testid="platform-login-email"
          {...register('email')}
        />
      </Field>
      <Field
        label={t('panel:platform.login.password')}
        htmlFor="pl-password"
        error={errors.password?.message ? `errors:${errors.password.message}` : undefined}
      >
        <Input
          id="pl-password"
          type="password"
          autoComplete="current-password"
          invalid={!!errors.password}
          data-testid="platform-login-password"
          {...register('password')}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={isSubmitting}
        data-testid="platform-login-submit"
      >
        {t('panel:platform.login.submit')}
      </Button>
    </form>
  );
}

function CodeForm({
  challengeToken,
  onVerified,
  onRestart,
  enrolling,
}: {
  challengeToken: string;
  onVerified: (s: PlatformSession) => void;
  onRestart: () => void;
  enrolling?: boolean;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const {
    watch,
    setValue,
    handleSubmit,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<CodeInput>({ resolver: zodResolver(totpCodeSchema), defaultValues: { code: '' } });
  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const s = await unwrap(
        platformApi.POST('/platform/auth/verify', {
          body: { challengeToken, code: v.code.trim().replace(/\s+/g, '') },
        }),
      );
      onVerified(s);
    } catch (e) {
      if (isApiClientError(e, 'validation_failed'))
        setFieldError('code', { message: 'panel:platform.login.codeInvalid' });
      else if (isApiClientError(e) && e.status === 401) {
        // Izazov je istekao: prijava ispočetka.
        setError(t('panel:platform.login.challengeExpired'));
      } else if (isApiClientError(e)) setError(t(e.i18nKey));
      else setError(t('errors:network_error'));
    }
  });
  const codeError = errors.code?.message
    ? errors.code.message.includes(':')
      ? errors.code.message
      : `errors:${errors.code.message}`
    : undefined;
  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      noValidate
      className="flex flex-col gap-4"
      data-testid="platform-totp-form"
    >
      {enrolling ? null : (
        <p className="m-0 text-sm text-text2">{t('panel:platform.login.totpIntro')}</p>
      )}
      <ErrorLine message={error} />
      <Field
        label={t('panel:platform.login.code')}
        htmlFor="pl-code"
        error={codeError}
        hint={t('panel:platform.login.codeHint')}
      >
        <OtpInput
          id="pl-code"
          testId="platform-totp-code"
          autoSubmit
          value={watch('code')}
          onChange={(value) => setValue('code', value)}
          invalid={!!errors.code}
          allowBackup={!enrolling}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={isSubmitting}
        data-testid="platform-totp-submit"
      >
        {enrolling ? t('panel:platform.login.enableTotp') : t('panel:platform.login.verify')}
      </Button>
      <Button variant="link" className="self-center" onClick={onRestart}>
        {t('panel:platform.login.back')}
      </Button>
    </form>
  );
}
